import { useEffect, useMemo, useState } from "react";
import { Navigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
} from "@mui/material";
import DataTable, { ColumnOptions } from "components/DataTable";
import OutlinedCard from "components/OutlinedCard";
import PageLayout from "components/PageLayout";
import PageTitle from "components/PageTitle";
import { SimpleOrder } from "models/SimpleOrder";
import { formatDateTime } from "utilities/formatDateTime";
import { useGetOrdersInfiniteQuery } from "app/api/orders";
import { useAuthContext } from "features/auth/AuthContext";
import { useGetAllOfficesQuery } from "app/api/offices";
import { Office } from "models/Office";
import DataLabel from "components/DataLabel";
import { UserRole } from "features/auth/types/user";
import LoadingDisplay from "components/LoadingDisplay";
import Refresh from "@mui/icons-material/Refresh";
import { SortBy, SortDir } from "api/orders/getOrders";

const columns = [
  {
    label: "Ordernummer",
    key: "ordernr",
    dense: true,
    renderCell: (row) => (
      <Link component={RouterLink} to={`/orders/view/${row.ordernr}`}>
        {row.ordernr}
      </Link>
    ),
  },
  { label: "Kund", key: "companyName" },
  {
    label: "Status ändrad",
    key: "lastStatusUpdate",
    align: "right",
    dense: true,
    defaultSortDirection: "desc",
    renderCell: (row) => (row.lastStatusUpdate ? formatDateTime(row.lastStatusUpdate) : null),
  },
  {
    label: "Granskad",
    key: "reviewedAt",
    align: "right",
    dense: true,
    defaultSortDirection: "desc",
    renderCell: (row) => (row.reviewedAt ? formatDateTime(row.reviewedAt) : null),
  },
] satisfies ColumnOptions<SimpleOrder>;

const OrderReviewPage: React.FC = () => {
  const { user, hasRole } = useAuthContext();

  const [selectedOffice, setSelectedOffice] = useState<Office | null>(user?.office ?? null);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [sortBy, setSortBy] = useState<SortBy>("lastStatusUpdate");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const { data: offices } = useGetAllOfficesQuery();
  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } = useGetOrdersInfiniteQuery(
    {
      selectedForReview: true,
      coordinator: selectedOffice?.name || null,
      reviewed: includeReviewed ? null : false,
      sortBy,
      sortDir,
    },
    { skip: !user || (!user.isOrderReviewer && !hasRole([UserRole.ADMIN])) }
  );

  useEffect(() => {
    if (offices && user?.office) {
      const office = offices.find((o) => o.name === user.office?.name) || null;
      setSelectedOffice(office);
    }
  }, [offices, user]);

  const orders = useMemo(() => data?.pages.flat() || [], [data]);

  function handleSortChange(sortBy: string, sortDir: SortDir) {
    setSortBy(sortBy as SortBy);
    setSortDir(sortDir);
  }

  if (!user || (!user.isOrderReviewer && !hasRole([UserRole.ADMIN])))
    return <Navigate to="/" replace />;

  return (
    <PageLayout>
      <PageTitle>Ordergranskning</PageTitle>

      <OutlinedCard>
        <Box display="flex" alignItems="center" justifyContent="start" gap={2} mb={1}>
          <FormControl variant="filled" sx={{ width: "100%", maxWidth: "20ch" }}>
            <InputLabel>Kontor</InputLabel>
            <Select
              value={selectedOffice?.name || ""}
              onChange={(e) => {
                const office = offices?.find((o) => o.name === e.target.value) || null;
                setSelectedOffice(office);
              }}
            >
              <MenuItem value="" disabled>
                Välj kontor
              </MenuItem>
              {offices?.map((office) => (
                <MenuItem key={office.name} value={office.name}>
                  {office.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <DataLabel label="Ansvarig granskare">
            {selectedOffice?.orderReviewer ? (
              <Link
                component={RouterLink}
                to={`/users/view/${selectedOffice.orderReviewer.userId}`}
                sx={{ fontSize: "inherit", fontWeight: "normal" }}
              >
                {selectedOffice.orderReviewer.userName}
              </Link>
            ) : (
              "Ingen för detta kontor"
            )}
          </DataLabel>

          <FormControlLabel
            label="Inkludera granskade"
            sx={{ ml: "auto" }}
            control={
              <Checkbox
                checked={includeReviewed}
                onChange={(e) => setIncludeReviewed(e.target.checked)}
              />
            }
          />
        </Box>

        {isLoading ? (
          <LoadingDisplay message="Hämtar ordrar..." />
        ) : (
          <DataTable
            columns={columns}
            data={orders ?? []}
            getKey={(row) => row.ordernr}
            sortMode="server"
            sort={{ sortBy, sortDir }}
            onSortChange={handleSortChange}
            striped
            noBottomBorder
            emptyMessage="Inga ordrar att granska"
          />
        )}
        {hasNextPage && (
          <Box display="flex" justifyContent="center" mt={1}>
            <Button
              variant="text"
              onClick={() => fetchNextPage()}
              startIcon={<Refresh />}
              loading={isFetching}
              loadingPosition="start"
            >
              Hämta fler ordrar
            </Button>
          </Box>
        )}
      </OutlinedCard>
    </PageLayout>
  );
};

export default OrderReviewPage;
